import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useWorkspace } from "../context/WorkspaceContext";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import {
  Plus, Search, Settings, Calendar, FileText,
  Clock, MoreHorizontal, Trash2, Edit2, Check, X,
  LogOut, ChevronDown, Grid, List, Building2, ChevronRight,
} from "lucide-react";

// ── Constants ────────────────────────────────────────────────────────────────
const COLORS = ["#5A90F5","#8B5CF6","#EC4899","#F59E0B","#10B981","#EF4444","#06B6D4","#F97316"];

const BANNER_GRADIENTS = {
  Vibrant: [
    { name: "Peachy Keen",   value: "linear-gradient(135deg, #f953c6 0%, #b91d73 100%)" },
    { name: "Hot & Cold",    value: "linear-gradient(135deg, #0575e6 0%, #021b79 100%)" },
    { name: "Mango Sorbet",  value: "linear-gradient(135deg, #ffe259 0%, #ffa751 100%)" },
    { name: "Aurora",        value: "linear-gradient(135deg, #00c6ff 0%, #0072ff 100%)" },
    { name: "Watermelon",    value: "linear-gradient(135deg, #ff6b6b 0%, #feca57 100%)" },
    { name: "Ultraviolet",   value: "linear-gradient(135deg, #834d9b 0%, #d04ed6 100%)" },
  ],
  Dark: [
    { name: "Midnight",      value: "linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)" },
    { name: "Obsidian",      value: "linear-gradient(135deg, #000000 0%, #1a1a2e 100%)" },
    { name: "Deep Sea",      value: "linear-gradient(135deg, #0a0a0a 0%, #0d2137 50%, #0f4c75 100%)" },
    { name: "Ember",         value: "linear-gradient(135deg, #0d0d0d 0%, #2d0a0a 50%, #4a1010 100%)" },
    { name: "Forrest",       value: "linear-gradient(135deg, #0a0d0a 0%, #0a2d0a 50%, #0a3d1a 100%)" },
    { name: "Void",          value: "linear-gradient(135deg, #000000 0%, #130f40 100%)" },
  ],
  Pastel: [
    { name: "Cotton Candy",  value: "linear-gradient(135deg, #fccb90 0%, #d57eeb 100%)" },
    { name: "Morning Mist",  value: "linear-gradient(135deg, #d3cce3 0%, #e9e4f0 100%)" },
    { name: "Peach Fuzz",    value: "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)" },
    { name: "Lavender Dream",value: "linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)" },
    { name: "Mint Chip",     value: "linear-gradient(135deg, #d4fc79 0%, #96e6a1 100%)" },
    { name: "Dreamsicle",    value: "linear-gradient(135deg, #f7971e 0%, #ffd200 100%)" },
  ],
};

const SOLID_COLORS = {
  "Brand": ["#5A90F5","#8B5CF6","#EC4899","#F59E0B","#10B981","#EF4444","#06B6D4","#F97316"],
  "Neutrals": ["#1a1a1a","#2a2a2a","#3a3a3a","#18181b","#27272a","#141414","#0d0d0d","#111111"],
  "Deep": ["#0f172a","#1e1b4b","#0c1445","#1a0533","#1c0a00","#0a1a0a","#0a0a1a","#1a0a0a"],
};

// ── Helpers ──────────────────────────────────────────────────────────────────
function getBanner(id) { try { return JSON.parse(localStorage.getItem(`cresc:banner:${id}`) || "null"); } catch { return null; } }
function setBannerStorage(id, b) { localStorage.setItem(`cresc:banner:${id}`, JSON.stringify(b)); }
function getLastOpened(id) { return localStorage.getItem(`cresc:opened:${id}`); }
function markOpened(id) { localStorage.setItem(`cresc:opened:${id}`, new Date().toISOString()); }

function timeAgo(d) {
  if (!d) return "";
  const s = (Date.now() - new Date(d)) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s/60)}m ago`;
  if (s < 86400) return `${Math.floor(s/3600)}h ago`;
  if (s < 2592000) return `${Math.floor(s/86400)}d ago`;
  return new Date(d).toLocaleDateString();
}

function formatEventDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (d - Date.now()) / 86400000;
  if (diff < 1) return "Today " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (diff < 2) return "Tomorrow " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

// ── BannerPicker ─────────────────────────────────────────────────────────────
function BannerPicker({ projectId, color, onClose }) {
  const [tab, setTab] = useState("gradient"); // gradient | color | image
  const [gradSection, setGradSection] = useState(null); // null = all categories
  const [customGrad, setCustomGrad] = useState("");
  const [customColor, setCustomColor] = useState(color);
  const [imageTab, setImageTab] = useState("unsplash"); // unsplash | upload
  const [unsplashQuery, setUnsplashQuery] = useState("abstract");
  const [unsplashResults, setUnsplashResults] = useState([]);
  const [unsplashLoading, setUnsplashLoading] = useState(false);
  const [uploads, setUploads] = useState(() => { try { return JSON.parse(localStorage.getItem("cresc:banner_uploads") || "[]"); } catch { return []; } });
  const ref = useRef(null);

  useEffect(() => {
    const h = e => { if (!ref.current?.contains(e.target)) onClose(getBanner(projectId)); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [onClose, projectId]);

  const searchUnsplash = async (q) => {
    setUnsplashLoading(true);
    try {
      const res = await fetch(`https://api.unsplash.com/search/photos?query=${encodeURIComponent(q)}&per_page=12&orientation=landscape`, {
        headers: { Authorization: "Client-ID YOUR_UNSPLASH_KEY" }
      });
      const data = await res.json();
      setUnsplashResults(data.results || []);
    } catch { setUnsplashResults([]); }
    finally { setUnsplashLoading(false); }
  };

  const apply = (banner) => { setBannerStorage(projectId, banner); onClose(banner); };

  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const url = ev.target.result;
      const next = [url, ...uploads].slice(0, 12);
      setUploads(next);
      localStorage.setItem("cresc:banner_uploads", JSON.stringify(next));
      apply({ type: "image", value: url });
    };
    reader.readAsDataURL(file);
  };

  const TABS = [
    { id: "gradient", label: "Gradient" },
    { id: "color",    label: "Color" },
    { id: "image",    label: "Image" },
  ];

  return (
    <div className="fixed inset-0 z-[200] flex items-start justify-start" onClick={() => onClose(getBanner(projectId))}>
      <div ref={ref} onClick={e => e.stopPropagation()}
        className="mt-32 ml-8 bg-[#111111] border border-[#2A2A2A] rounded-2xl shadow-2xl w-72 overflow-hidden">

        {/* Tab list — vertical left-side nav */}
        <div className="flex">
          {/* Left nav */}
          <div className="w-24 bg-[#0D0D0D] border-r border-[#1E1E1E] py-2 flex flex-col gap-0.5">
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`text-left px-3 py-2 text-[12px] transition-colors ${tab === t.id ? "text-[#EDEDED] bg-[#1a1a1a]" : "text-[#52525B] hover:text-[#A1A1AA]"}`}>
                {t.label}
              </button>
            ))}
            <div className="mt-auto pt-2 border-t border-[#1E1E1E] mx-2">
              <button onClick={() => apply(null)} className="w-full text-left px-1 py-1.5 text-[10px] text-[#3A3A3A] hover:text-[#52525B] transition-colors">
                Remove
              </button>
            </div>
          </div>

          {/* Right content */}
          <div className="flex-1 p-3 max-h-96 overflow-y-auto">

            {tab === "gradient" && (
              <div className="space-y-3">
                {Object.entries(BANNER_GRADIENTS).map(([cat, grads]) => (
                  <div key={cat}>
                    <p className="text-[9px] uppercase tracking-widest text-[#3A3A3A] mb-1.5">{cat}</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {grads.map(g => (
                        <button key={g.name} onClick={() => apply({ type: "gradient", value: g.value })}
                          className="group relative h-10 rounded-lg overflow-hidden ring-1 ring-white/5 hover:ring-white/20 transition-all"
                          style={{ background: g.value }}>
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <span className="text-[9px] text-white font-medium">{g.name}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                <div>
                  <p className="text-[9px] uppercase tracking-widest text-[#3A3A3A] mb-1.5">Custom</p>
                  <div className="flex gap-1.5">
                    <input value={customGrad} onChange={e => setCustomGrad(e.target.value)}
                      placeholder="linear-gradient(…)"
                      className="flex-1 bg-[#0A0A0A] border border-[#2A2A2A] rounded px-2 py-1 text-[10px] text-[#EDEDED] placeholder-[#3A3A3A] outline-none focus:border-[#5A90F5]"/>
                    <button onClick={() => customGrad.trim() && apply({ type: "gradient", value: customGrad.trim() })}
                      className="px-2 py-1 rounded bg-[#5A90F5] text-white text-[10px]">→</button>
                  </div>
                </div>
              </div>
            )}

            {tab === "color" && (
              <div className="space-y-3">
                {Object.entries(SOLID_COLORS).map(([cat, cols]) => (
                  <div key={cat}>
                    <p className="text-[9px] uppercase tracking-widest text-[#3A3A3A] mb-1.5">{cat}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {cols.map(c => (
                        <button key={c} onClick={() => apply({ type: "gradient", value: c })}
                          className="w-7 h-7 rounded-md ring-1 ring-white/10 hover:ring-white/30 hover:scale-110 transition-all"
                          style={{ background: c }}/>
                      ))}
                    </div>
                  </div>
                ))}
                <div>
                  <p className="text-[9px] uppercase tracking-widest text-[#3A3A3A] mb-1.5">Custom</p>
                  <div className="flex gap-1.5 items-center">
                    <input type="color" value={customColor} onChange={e => setCustomColor(e.target.value)}
                      className="w-8 h-8 rounded cursor-pointer bg-transparent border-0 p-0"/>
                    <button onClick={() => apply({ type: "gradient", value: customColor })}
                      className="px-2 py-1 rounded bg-[#5A90F5] text-white text-[10px]">Apply</button>
                  </div>
                </div>
              </div>
            )}

            {tab === "image" && (
              <div>
                <div className="flex gap-1 mb-3 bg-[#0A0A0A] rounded-md p-0.5">
                  {["unsplash", "upload"].map(t => (
                    <button key={t} onClick={() => setImageTab(t)}
                      className={`flex-1 py-1 rounded text-[10px] capitalize transition-colors ${imageTab === t ? "bg-[#1a1a1a] text-[#EDEDED]" : "text-[#52525B] hover:text-[#A1A1AA]"}`}>
                      {t}
                    </button>
                  ))}
                </div>

                {imageTab === "unsplash" && (
                  <>
                    <div className="flex gap-1 mb-2">
                      <input value={unsplashQuery} onChange={e => setUnsplashQuery(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && searchUnsplash(unsplashQuery)}
                        placeholder="Search Unsplash…"
                        className="flex-1 bg-[#0A0A0A] border border-[#2A2A2A] rounded px-2 py-1 text-[10px] text-[#EDEDED] placeholder-[#3A3A3A] outline-none focus:border-[#5A90F5]"/>
                      <button onClick={() => searchUnsplash(unsplashQuery)}
                        className="px-2 py-1 rounded bg-[#5A90F5] text-white text-[10px]">Go</button>
                    </div>
                    {unsplashLoading ? (
                      <p className="text-[10px] text-[#3A3A3A] text-center py-4">Searching…</p>
                    ) : unsplashResults.length > 0 ? (
                      <div className="grid grid-cols-2 gap-1">
                        {unsplashResults.map(img => (
                          <button key={img.id} onClick={() => apply({ type: "image", value: img.urls.regular })}
                            className="h-14 rounded-md overflow-hidden ring-1 ring-white/5 hover:ring-white/20 transition-all"
                            style={{ backgroundImage: `url(${img.urls.small})`, backgroundSize: "cover", backgroundPosition: "center" }}/>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[10px] text-[#3A3A3A] text-center py-4">Search for images above</p>
                    )}
                  </>
                )}

                {imageTab === "upload" && (
                  <>
                    <label className="block w-full py-2 rounded-lg border border-dashed border-[#2A2A2A] text-center text-[10px] text-[#52525B] hover:border-[#3A3A3A] hover:text-[#A1A1AA] cursor-pointer transition-colors mb-2">
                      Upload image
                      <input type="file" accept="image/*" className="hidden" onChange={handleUpload}/>
                    </label>
                    {uploads.length > 0 && (
                      <div className="grid grid-cols-2 gap-1">
                        {uploads.map((url, i) => (
                          <button key={i} onClick={() => apply({ type: "image", value: url })}
                            className="h-14 rounded-md overflow-hidden ring-1 ring-white/5 hover:ring-white/20 transition-all"
                            style={{ backgroundImage: `url(${url})`, backgroundSize: "cover", backgroundPosition: "center" }}/>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}

// ── CardMenu ─────────────────────────────────────────────────────────────────
function CardMenu({ onRename, onDelete, onSetBanner, onClose, anchorRef }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (anchorRef?.current) {
      const r = anchorRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: Math.max(8, r.right - 160) });
    }
    const h = e => { if (!ref.current?.contains(e.target) && !anchorRef?.current?.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [onClose, anchorRef]);

  return createPortal(
    <div ref={ref} style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 9999 }}
      className="bg-[#141414] border border-[#2A2A2A] rounded-xl shadow-2xl w-40 py-1 overflow-hidden">
      <button onClick={onRename} className="w-full flex items-center gap-2 px-3 py-2 text-[11px] text-[#A1A1AA] hover:bg-[#1a1a1a] hover:text-[#EDEDED] transition-colors">
        <Edit2 className="w-3 h-3"/> Rename
      </button>
      <button onClick={onSetBanner} className="w-full flex items-center gap-2 px-3 py-2 text-[11px] text-[#A1A1AA] hover:bg-[#1a1a1a] hover:text-[#EDEDED] transition-colors">
        <FileText className="w-3 h-3"/> Set banner
      </button>
      <div className="h-px bg-[#1E1E1E] mx-2 my-1"/>
      <button onClick={onDelete} className="w-full flex items-center gap-2 px-3 py-2 text-[11px] text-[#EF4444] hover:bg-[#1a1a1a] transition-colors">
        <Trash2 className="w-3 h-3"/> Delete
      </button>
    </div>,
    document.body
  );
}

// ── ProjectCard ───────────────────────────────────────────────────────────────
function ProjectCard({ project, onOpen, onDelete, onRename, view, orgMembers }) {
  const [menu, setMenu] = useState(false);
  const menuBtnRef = useRef(null);
  const [renaming, setRenaming] = useState(false);
  const [nameVal, setNameVal] = useState(project.name || "");
  const [banner, setBannerState] = useState(() => getBanner(project.project_id));
  const [showBannerPicker, setShowBannerPicker] = useState(false);
  const color = project.color || "#5A90F5";
  const displayName = project.name || "Unnamed project";
  const isUnnamed = !project.name;
  const lastOpened = getLastOpened(project.project_id);
  const avatars = (orgMembers || []).slice(0, 3);

  const doRename = () => { onRename(project.project_id, nameVal.trim() || null); setRenaming(false); };
  const handleOpen = () => { markOpened(project.project_id); onOpen(project.project_id); };
  const handleBannerSet = (b) => { setBannerStorage(project.project_id, b); setBannerState(b); setShowBannerPicker(false); };

  const bannerStyle = banner
    ? banner.type === "image"
      ? { backgroundImage: `url(${banner.value})`, backgroundSize: "cover", backgroundPosition: "center" }
      : { background: banner.value }
    : { background: `linear-gradient(135deg, #111111 0%, ${color}20 100%)` };

  if (view === "list") {
    return (
      <div className="group flex items-center gap-3 px-4 py-3 rounded-lg border border-transparent hover:border-[#1E1E1E] hover:bg-[#0D0D0D] cursor-pointer transition-all"
        onClick={() => !menu && !renaming && !showBannerPicker && handleOpen()}>
        <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }}/>
        {renaming ? (
          <input autoFocus value={nameVal} onChange={e => setNameVal(e.target.value)}
            onKeyDown={e => { if (e.key==="Enter") doRename(); if (e.key==="Escape") setRenaming(false); }}
            onClick={e => e.stopPropagation()}
            className="flex-1 bg-[#1a1a1a] border border-[#5A90F5] rounded px-2 py-0.5 text-[13px] text-[#EDEDED] outline-none"/>
        ) : (
          <span className={`flex-1 text-[13px] font-medium truncate ${isUnnamed ? "text-[#52525B] italic" : "text-white"}`}>{displayName}</span>
        )}
        <div className="flex items-center gap-4 text-[11px] text-[#52525B] flex-shrink-0">
          {avatars.length > 0 && (
            <div className="flex -space-x-1">
              {avatars.map((m, i) => (
                <div key={i} className="w-5 h-5 rounded-full border border-[#080808] flex items-center justify-center text-[8px] font-bold"
                  style={{ background: COLORS[i % COLORS.length] + "80", color: "white" }}>
                  {(m.name || m.email || "?")[0]?.toUpperCase()}
                </div>
              ))}
            </div>
          )}
          <span>{project.workspace_count ?? 0} ws</span>
          <span>{project.document_count ?? 0} docs</span>
          {project.org_name && <span className="flex items-center gap-1"><Building2 className="w-3 h-3"/>{project.org_name}</span>}
          {lastOpened && <span className="text-[#3A3A3A]">opened {timeAgo(lastOpened)}</span>}
          <span className="text-[#3A3A3A]">{timeAgo(project.updated_at)}</span>
        </div>
        <div className="relative flex-shrink-0" onClick={e => e.stopPropagation()}>
          <button ref={menuBtnRef} onClick={() => setMenu(v=>!v)} className="p-1 rounded opacity-0 group-hover:opacity-100 text-[#52525B] hover:text-[#A1A1AA] hover:bg-[#1a1a1a] transition-all">
            <MoreHorizontal className="w-4 h-4"/>
          </button>
          {menu && <CardMenu anchorRef={menuBtnRef} onRename={() => { setRenaming(true); setMenu(false); }} onDelete={() => { onDelete(project.project_id); setMenu(false); }} onSetBanner={() => { setShowBannerPicker(true); setMenu(false); }} onClose={() => setMenu(false)}/>}
        </div>
        {showBannerPicker && <BannerPicker projectId={project.project_id} color={color} onClose={handleBannerSet}/>}
      </div>
    );
  }

  // Grid card
  return (
    <>
      {showBannerPicker && <BannerPicker projectId={project.project_id} color={color} onClose={handleBannerSet}/>}
      <div className="group relative rounded-xl overflow-hidden cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-black/40"
        style={{ background: "#0D0D0D", border: "1px solid #1A1A1A" }}
        onClick={() => !menu && !renaming && !showBannerPicker && handleOpen()}>

        {/* Banner */}
        <div className="relative h-[72px] overflow-hidden" style={bannerStyle}>
          <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, transparent, rgba(0,0,0,0.6))" }}/>

          {/* Menu button — top right of banner */}
          <div className="absolute top-2 right-2 z-10" onClick={e => e.stopPropagation()}>
            <div className="relative">
              <button onClick={() => setMenu(v=>!v)}
                className="p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(8px)" }}>
                <MoreHorizontal className="w-3.5 h-3.5 text-white/70"/>
              </button>
              {menu && <CardMenu anchorRef={menuBtnRef} onRename={() => { setRenaming(true); setMenu(false); }} onDelete={() => { onDelete(project.project_id); setMenu(false); }} onSetBanner={() => { setShowBannerPicker(true); setMenu(false); }} onClose={() => setMenu(false)}/>}
            </div>
          </div>

          {/* Collaborator avatars */}
          {avatars.length > 0 && (
            <div className="absolute bottom-2 right-3 flex -space-x-1.5">
              {avatars.map((m, i) => (
                <div key={i} title={m.email || m.name}
                  className="w-6 h-6 rounded-full border border-black/50 flex items-center justify-center text-[9px] font-bold"
                  style={{ background: COLORS[i % COLORS.length] + "90", color: "white" }}>
                  {(m.name || m.email || "?")[0]?.toUpperCase()}
                </div>
              ))}
              {(orgMembers?.length || 0) > 3 && (
                <div className="w-6 h-6 rounded-full border border-black/50 flex items-center justify-center text-[8px] font-bold text-white/60"
                  style={{ background: "rgba(255,255,255,0.15)" }}>
                  +{orgMembers.length - 3}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Body */}
        <div className="p-3.5">
          {/* Name */}
          {renaming ? (
            <div className="flex items-center gap-1 mb-2" onClick={e => e.stopPropagation()}>
              <input autoFocus value={nameVal} onChange={e => setNameVal(e.target.value)}
                onKeyDown={e => { if (e.key==="Enter") doRename(); if (e.key==="Escape") setRenaming(false); }}
                className="flex-1 bg-[#0A0A0A] border border-[#5A90F5] rounded px-2 py-0.5 text-[12px] text-white outline-none"/>
              <button onClick={doRename} className="text-[#5A90F5]"><Check className="w-3.5 h-3.5"/></button>
              <button onClick={() => setRenaming(false)} className="text-[#52525B]"><X className="w-3.5 h-3.5"/></button>
            </div>
          ) : (
            <h3 className={`text-[13px] font-semibold truncate mb-0.5 ${isUnnamed ? "text-[#52525B] italic" : "text-white"}`}>{displayName}</h3>
          )}

          {/* Last opened + org */}
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[10px] text-[#52525B]">{lastOpened ? `Opened ${timeAgo(lastOpened)}` : "Never opened"}</span>
            {project.org_name && (
              <><span className="text-[#2A2A2A]">·</span>
              <span className="flex items-center gap-1 text-[10px] text-[#52525B]">
                <Building2 className="w-2.5 h-2.5"/>{project.org_name}
              </span></>
            )}
          </div>

          {/* Stats */}
          <div className="flex items-center gap-1.5 mb-3">
            {[
              { icon: <Calendar className="w-2.5 h-2.5"/>, label: `${project.workspace_count ?? 0} workspaces` },
              { icon: <FileText className="w-2.5 h-2.5"/>, label: `${project.document_count ?? 0} docs` },
            ].map((s, i) => (
              <div key={i} className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] text-[#52525B]"
                style={{ background: "#141414", border: "1px solid #1E1E1E" }}>
                {s.icon} {s.label}
              </div>
            ))}
            {project.updated_at && (
              <div className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] text-[#3A3A3A] ml-auto"
                style={{ background: "#111111", border: "1px solid #1A1A1A" }}>
                <Clock className="w-2.5 h-2.5"/> {timeAgo(project.updated_at)}
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="mb-3 h-px bg-[#1A1A1A]"/>

          {/* Next event */}
          {project.next_event ? (
            <div className="flex items-center gap-2 mb-1.5">
              <div className="w-1 h-1 rounded-full flex-shrink-0" style={{ background: color }}/>
              <span className="text-[10px] text-[#A1A1AA] truncate flex-1">{project.next_event.title}</span>
              <span className="text-[9px] flex-shrink-0 text-[#52525B]">{formatEventDate(project.next_event.start)}</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 mb-1.5">
              <div className="w-1 h-1 rounded-full flex-shrink-0 bg-[#2A2A2A]"/>
              <span className="text-[10px] text-[#3A3A3A]">No upcoming events</span>
            </div>
          )}

          {/* Recent doc */}
          {project.recent_doc ? (
            <div className="flex items-center gap-2">
              <FileText className="w-3 h-3 flex-shrink-0 text-[#3A3A3A]"/>
              <span className="text-[10px] text-[#A1A1AA] truncate flex-1">{project.recent_doc.name}</span>
              <span className="text-[9px] text-[#3A3A3A] flex-shrink-0">{timeAgo(project.recent_doc.updated_at)}</span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <FileText className="w-3 h-3 flex-shrink-0 text-[#2A2A2A]"/>
              <span className="text-[10px] text-[#3A3A3A]">No documents yet</span>
            </div>
          )}

          {/* Quick actions */}
          <div className="flex gap-1.5 mt-3 pt-3 border-t border-[#1A1A1A] opacity-0 group-hover:opacity-100 transition-all duration-200"
            onClick={e => e.stopPropagation()}>
            <button onClick={handleOpen}
              className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-[10px] font-medium transition-all"
              style={{ background: `${color}18`, color, border: `1px solid ${color}25` }}>
              <Calendar className="w-3 h-3"/> Calendar
            </button>
            <button onClick={handleOpen}
              className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-[10px] font-medium transition-all text-[#52525B] hover:text-[#A1A1AA]"
              style={{ background: "#141414", border: "1px solid #1E1E1E" }}>
              <FileText className="w-3 h-3"/> Docs
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ── ProfileTray ───────────────────────────────────────────────────────────────
function ProfileTray({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = e => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const initials = user?.name
    ? user.name.split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2)
    : user?.email?.[0]?.toUpperCase() || "?";

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(v=>!v)}
        className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-[#141414] transition-colors">
        <div className="w-7 h-7 rounded-full bg-[#5A90F5]/20 border border-[#5A90F5]/30 flex items-center justify-center text-[11px] font-semibold text-[#5A90F5]">
          {initials}
        </div>
        <span className="text-[12px] text-[#A1A1AA] max-w-[120px] truncate hidden sm:block">{user?.name || user?.email}</span>
        <ChevronDown className="w-3 h-3 text-[#52525B]"/>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 bg-[#141414] border border-[#2A2A2A] rounded-xl shadow-2xl w-56 overflow-hidden py-1">
          <div className="px-3 py-3 border-b border-[#1a1a1a]">
            <p className="text-[12px] font-medium text-white truncate">{user?.name || "User"}</p>
            <p className="text-[10px] text-[#52525B] truncate">{user?.email}</p>
          </div>
          <button onClick={() => { setOpen(false); onLogout(); }}
            className="w-full flex items-center gap-2 px-3 py-2.5 text-[12px] text-[#EF4444] hover:bg-[#1a1a1a] transition-colors">
            <LogOut className="w-3.5 h-3.5"/> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

// ── ProjectHome ───────────────────────────────────────────────────────────────
export default function ProjectHome() {
  const { setCurrent } = useWorkspace();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orgMembers, setOrgMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [view, setView] = useState("grid");
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(COLORS[0]);
  const [newDesc, setNewDesc] = useState("");
  const [saving, setSaving] = useState(false);

  const [debugInfo, setDebugInfo] = useState(null);
  const scrollRef = useRef(null);
  const touchStartY = useRef(0);
  const [pullIndicator, setPullIndicator] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setDebugInfo(null);
    try {
      let projStatus = null, projData = null, wsStatus = null, wsData = null;

      try {
        const r = await api.get("/projects");
        projStatus = r.status;
        projData = r.data;
      } catch (e) {
        projStatus = e?.response?.status || "network error";
      }

      if (Array.isArray(projData)) {
        setProjects(projData);
        setDebugInfo(`/projects → ${projStatus}, ${projData.length} items`);
      } else {
        // fallback to workspaces
        try {
          const r2 = await api.get("/workspaces");
          wsStatus = r2.status;
          wsData = r2.data;
        } catch (e) {
          wsStatus = e?.response?.status || "network error";
        }

        if (Array.isArray(wsData) && wsData.length > 0) {
          setProjects(wsData.map(ws => ({
            project_id: ws.workspace_id,
            name: ws.name,
            color: ws.color || "#5A90F5",
            description: ws.description || "",
            updated_at: ws.updated_at,
            workspace_count: 1,
            document_count: 0,
            _isWorkspace: true,
          })));
          setDebugInfo(`/projects → ${projStatus} | /workspaces → ${wsStatus}, ${wsData?.length} items`);
        } else {
          setProjects([]);
          setDebugInfo(`/projects → ${projStatus} | /workspaces → ${wsStatus}, ${wsData?.length ?? 0} items`);
        }
      }
    } catch (err) {
      setDebugInfo(`Outer error: ${err?.message}`);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = (projects || []).filter(p =>
    (p.name || "unnamed project").toLowerCase().includes(search.toLowerCase())
  );

  const handleOpen = (projectId) => {
    const project = projects.find(p => p.project_id === projectId);
    if (project?._isWorkspace) {
      // Fallback mode — project_id is actually a workspace_id
      setCurrent(projectId);
    } else {
      localStorage.setItem("crescendo:project_id", projectId);
    }
    navigate("/app");
  };

  const handleCreate = async () => {
    if (saving) return;
    setSaving(true);
    try {
      // Try real projects API first, fall back to creating a workspace
      try {
        const { data } = await api.post("/projects", { name: newName.trim() || null, color: newColor, description: newDesc.trim() });
        localStorage.setItem("crescendo:project_id", data.project_id);
      } catch {
        // Fallback: create workspace directly
        const { data } = await api.post("/workspaces", { name: newName.trim() || "New Project", color: newColor, description: newDesc.trim() });
        setCurrent(data.workspace_id);
      }
      setCreating(false); setNewName(""); setNewDesc(""); setNewColor(COLORS[0]);
      navigate("/app");
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (projects.length <= 1) { alert("You can't delete your only project."); return; }
    if (!window.confirm("Delete this project and all its workspaces and documents?")) return;
    try {
      await api.delete(`/projects/${id}`);
    } catch {
      await api.delete(`/workspaces/${id}`);
    }
    await load();
  };

  const handleRename = async (id, name) => {
    await api.patch(`/projects/${id}`, { name });
    setProjects(prev => prev.map(p => p.project_id === id ? { ...p, name } : p));
  };

  return (
    <div className="h-screen flex flex-col bg-[#080808] text-[#EDEDED]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Top bar */}
      <div className="flex-shrink-0 h-12 border-b border-[#141414] flex items-center px-4 gap-3">
        <span className="text-[14px] font-bold tracking-tight text-white mr-2 flex-shrink-0" style={{ fontFamily: "'Outfit', sans-serif" }}>crescendo</span>
        <div className="h-4 w-px bg-[#1E1E1E] flex-shrink-0"/>
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-[#2A2A2A]"/>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search projects…"
            className="w-full bg-[#0D0D0D] border border-[#141414] rounded-md pl-7 pr-3 py-1.5 text-[12px] text-[#EDEDED] placeholder-[#2A2A2A] outline-none focus:border-[#2A2A2A]"/>
        </div>
        <div className="flex-1"/>
        <div className="flex items-center gap-0.5 bg-[#0D0D0D] border border-[#141414] rounded-md p-0.5">
          <button onClick={() => setView("grid")} className={`p-1.5 rounded transition-colors ${view==="grid" ? "bg-[#1a1a1a] text-[#EDEDED]" : "text-[#3A3A3A] hover:text-[#A1A1AA]"}`}><Grid className="w-3.5 h-3.5"/></button>
          <button onClick={() => setView("list")} className={`p-1.5 rounded transition-colors ${view==="list" ? "bg-[#1a1a1a] text-[#EDEDED]" : "text-[#3A3A3A] hover:text-[#A1A1AA]"}`}><List className="w-3.5 h-3.5"/></button>
        </div>
        <button onClick={() => setCreating(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#5A90F5] text-white text-[12px] font-medium hover:bg-[#4a80e5] transition-colors flex-shrink-0">
          <Plus className="w-3.5 h-3.5"/> New project
        </button>
        <div className="h-4 w-px bg-[#1E1E1E] flex-shrink-0"/>
        <button onClick={() => navigate("/keybinds")} className="p-2 rounded-md text-[#3A3A3A] hover:text-[#A1A1AA] hover:bg-[#0D0D0D] transition-colors"><Settings className="w-4 h-4"/></button>
        <ProfileTray user={user} onLogout={logout}/>
      </div>

      {/* Sidebar + content */}
      <div className="flex-1 flex min-h-0">
        <div className="w-52 flex-shrink-0 border-r border-[#141414] flex flex-col py-3 px-2 gap-0.5">
          <p className="text-[9px] uppercase tracking-widest text-[#252525] px-2 mb-1">Projects</p>
          <button className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-[#0D0D0D] text-white text-[12px] font-medium">
            <Grid className="w-3.5 h-3.5 text-[#5A90F5]"/> All projects
          </button>
          {projects.length > 0 && (
            <div className="mt-3 pt-2 border-t border-[#141414]">
              <p className="text-[9px] uppercase tracking-widest text-[#252525] px-2 mb-1">Recent</p>
              {projects.slice(0,5).map(p => (
                <button key={p.project_id} onClick={() => handleOpen(p.project_id)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-[#52525B] hover:text-[#EDEDED] hover:bg-[#0D0D0D] text-[12px] transition-colors">
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: p.color || "#5A90F5" }}/>
                  <span className="truncate">{p.name || "Unnamed project"}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-6" ref={scrollRef}
          onTouchStart={e => { touchStartY.current = e.touches[0].clientY; }}
          onTouchMove={e => {
            const el = scrollRef.current;
            if (!el || el.scrollTop > 0) return;
            const dy = e.touches[0].clientY - touchStartY.current;
            if (dy > 60 && !loading) { setPullIndicator(true); }
          }}
          onTouchEnd={() => { if (pullIndicator) { load(); } setPullIndicator(false); }}
        >
          {pullIndicator && (
            <div className="flex justify-center mb-3">
              <span className="text-[11px] text-[#52525B] animate-pulse">Release to refresh…</span>
            </div>
          )}
          {debugInfo && (
            <div className="mb-3 px-3 py-2 rounded-lg bg-[#0D0D0D] border border-[#2A2A2A] flex items-center justify-between">
              <span className="text-[10px] font-mono text-[#52525B]">{debugInfo}</span>
              <button onClick={load} className="text-[10px] text-[#5A90F5] hover:underline ml-4">Retry</button>
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center h-32 text-[#252525] text-[12px]">Loading…</div>
          ) : filtered.length === 0 && search ? (
            <div className="flex flex-col items-center justify-center h-64 gap-2">
              <p className="text-[13px] text-[#3A3A3A]">No projects match "{search}"</p>
            </div>
          ) : view === "grid" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {filtered.map(p => (
                <ProjectCard key={p.project_id} project={p} view="grid"
                  onOpen={handleOpen} onDelete={handleDelete} onRename={handleRename} orgMembers={orgMembers}/>
              ))}
              <button onClick={() => setCreating(true)}
                className="bg-[#0D0D0D] border border-dashed border-[#1A1A1A] rounded-xl p-4 flex flex-col items-center justify-center gap-2 text-[#2A2A2A] hover:border-[#2A2A2A] hover:text-[#52525B] transition-all min-h-[140px]">
                <Plus className="w-5 h-5"/><span className="text-[12px]">New project</span>
              </button>
            </div>
          ) : (
            <div className="max-w-3xl">
              <div className="flex items-center px-4 py-2 mb-1 text-[9px] uppercase tracking-widest text-[#252525]">
                <span className="flex-1">Name</span><span className="w-32">Workspaces / Docs</span><span className="w-28">Modified</span><span className="w-8"/>
              </div>
              {filtered.map(p => (
                <ProjectCard key={p.project_id} project={p} view="list"
                  onOpen={handleOpen} onDelete={handleDelete} onRename={handleRename} orgMembers={orgMembers}/>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create modal */}
      {creating && (
        <>
          <div className="fixed inset-0 z-50 bg-black/80" onClick={() => setCreating(false)}/>
          <div className="fixed inset-0 z-[51] flex items-center justify-center p-4">
            <div className="bg-[#0D0D0D] border border-[#1E1E1E] rounded-2xl shadow-2xl w-full max-w-sm p-5" onClick={e => e.stopPropagation()}>
              <h3 className="text-[14px] font-semibold text-white mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>New project</h3>
              <p className="text-[11px] text-[#52525B] mb-4">A project has its own calendar workspaces and documents.</p>
              <div className="space-y-3">
                <input autoFocus value={newName} onChange={e => setNewName(e.target.value)}
                  onKeyDown={e => e.key==="Enter" && handleCreate()}
                  placeholder="Project name (optional)"
                  className="w-full bg-[#080808] border border-[#1E1E1E] rounded-lg px-3 py-2.5 text-[13px] text-white placeholder-[#2A2A2A] outline-none focus:border-[#5A90F5] transition-colors"/>
                <input value={newDesc} onChange={e => setNewDesc(e.target.value)}
                  placeholder="Description (optional)"
                  className="w-full bg-[#080808] border border-[#1E1E1E] rounded-lg px-3 py-2.5 text-[13px] text-white placeholder-[#2A2A2A] outline-none focus:border-[#5A90F5] transition-colors"/>
                <div>
                  <p className="text-[10px] text-[#52525B] mb-2">Color</p>
                  <div className="flex gap-2">
                    {COLORS.map(c => (
                      <button key={c} onClick={() => setNewColor(c)}
                        className="w-5 h-5 rounded-full transition-transform hover:scale-110 flex items-center justify-center"
                        style={{ background: c }}>
                        {newColor===c && <Check className="w-3 h-3 text-white"/>}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-5">
                <button onClick={() => setCreating(false)} className="flex-1 px-3 py-2 rounded-lg border border-[#1E1E1E] text-[12px] text-[#52525B] hover:bg-[#141414] transition-colors">Cancel</button>
                <button onClick={handleCreate} disabled={saving} className="flex-1 px-3 py-2 rounded-lg bg-[#5A90F5] text-white text-[12px] font-medium hover:bg-[#4a80e5] transition-colors disabled:opacity-40">
                  {saving ? "Creating…" : "Create project"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

class ProjectHomeErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(e) { return { error: e }; }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen bg-[#080808] flex items-center justify-center p-8">
          <div className="text-center">
            <p className="text-[#EF4444] text-[13px] mb-2">Something went wrong loading projects</p>
            <p className="text-[#3A3A3A] text-[11px] font-mono">{this.state.error?.message}</p>
            <button onClick={() => window.location.reload()} className="mt-4 px-4 py-2 rounded-lg bg-[#5A90F5] text-white text-[12px]">Reload</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Re-export wrapped in error boundary so crashes show useful info
const OriginalProjectHome = ProjectHome;
export { ProjectHomeErrorBoundary };
